import { DIMENSION_OPTION } from "../constants";
import { IQuestion } from "../types";

/**
 * Find parent questions that dependent questions rely on
 *
 * @param dependentQuestions - List of dependent questions that need to be answered
 * @param allQuestions - Complete question flow
 * @returns Array of parent questions that dependents rely on
 */
const findParentQuestions = (
  dependentQuestions: IQuestion[],
  allQuestions: IQuestion[],
): IQuestion[] => {
  const parentQuestions: IQuestion[] = [];
  const addedQuestionIds = new Set<string>();

  for (const dependentQuestion of dependentQuestions) {
    // Check if this question has dependsOn conditions
    if (
      dependentQuestion.displayCondition?.dependsOn &&
      dependentQuestion.displayCondition.dependsOn.length > 0
    ) {
      // For each dependency, find the parent question
      for (const dependency of dependentQuestion.displayCondition.dependsOn) {
        const parentQuestion = allQuestions.find(
          (q) => q.category === dependency.categoryId,
        );

        // Add parent question if found and not already added
        if (
          parentQuestion &&
          !addedQuestionIds.has(parentQuestion.questionId)
        ) {
          parentQuestions.push(parentQuestion);
          addedQuestionIds.add(parentQuestion.questionId);

        }
      }
    }
  }

  return parentQuestions;
};

/**
 * Recursively find all parent questions (including grandparents, etc.)
 *
 * @param questions - Questions to find parents for
 * @param allQuestions - Complete question flow
 * @param foundQuestionIds - Set to track already found questions (prevents duplicates)
 * @returns Array of all ancestor questions
 */
const findAllParentQuestionsRecursive = (
  questions: IQuestion[],
  allQuestions: IQuestion[],
  foundQuestionIds: Set<string> = new Set(),
): IQuestion[] => {
  const parentQuestions = findParentQuestions(questions, allQuestions);

  // Filter out already found questions
  const newParentQuestions = parentQuestions.filter(
    (q) => !foundQuestionIds.has(q.questionId),
  );

  if (newParentQuestions.length === 0) {
    // No new parents found, stop recursion
    return [];
  }

  // Mark these as found
  newParentQuestions.forEach((q) => foundQuestionIds.add(q.questionId));

  // Recursively find parents of these parent questions
  const grandParentQuestions = findAllParentQuestionsRecursive(
    newParentQuestions,
    allQuestions,
    foundQuestionIds,
  );

  return [...newParentQuestions, ...grandParentQuestions];
};

/**
 * Find and sort all questions that need to be shown when product changes
 * Includes both dependent questions and their parent questions
 *
 * @param dependentQuestions - Initial list of dependent questions
 * @param allQuestions - Complete question flow
 * @param isProductChanged - Whether the product has changed
 * @returns Sorted array of questions to show (by step order)
 */
export const findAndSortQuestionsForProductChange = (
  dependentQuestions: IQuestion[],
  allQuestions: IQuestion[],
  isProductChanged: boolean,
): IQuestion[] => {
  // If product hasn't changed, just return dependent questions as-is
  if (!isProductChanged) {
    return dependentQuestions;
  }


  // Track found question IDs to prevent duplicates
  const foundQuestionIds = new Set<string>(
    dependentQuestions.map((q) => q.questionId),
  );
  const dimensionQuestion = allQuestions?.filter(
    (item) => item.optionType === DIMENSION_OPTION,
  );
  // Find all parent questions recursively
  const parentQuestions = findAllParentQuestionsRecursive(
    dependentQuestions,
    allQuestions,
    foundQuestionIds,
  );


  // Combine dependent questions and parent questions
  const allRelatedQuestions = [...parentQuestions, ...dependentQuestions,...dimensionQuestion];

  // Remove duplicates (just in case)
  const uniqueQuestions = Array.from(
    new Map(allRelatedQuestions.map((q) => [q.questionId, q])).values(),
  );

  // Sort by step order
  const sortedQuestions = uniqueQuestions.sort((a, b) => {
    const stepA = a.step ?? 0;
    const stepB = b.step ?? 0;
    return stepA - stepB;
  });


  return sortedQuestions;
};

/**
 * Helper to log question hierarchy for debugging
 *
 * @param questions - Questions to log
 */
export const logQuestionHierarchy = (): void => {};
